import { warn } from '@execonline-inc/logging';
import { toTask } from '@execonline-inc/maybe-adapter';
import { InvalidUrlError, putQueryParam, toUrlT } from '@execonline-inc/url';
import { assertNever } from '@kofno/piper';
import { Maybe } from 'maybeasy';
import { Task } from 'taskarian';
import { AppyError, callApi } from '../../../../Appy';
import { currentUserStore } from '../../../../CurrentUserStore';
import ErrorActionableReaction, { EAProps, handleError } from '../../../../ErrorActionableReaction';
import { MissingLinkError, findLinkT } from '../../../../LinkyLinky';
import { Link } from '../../../../Resource/Types';
import { productLicensesResourceDecoder } from '../../Decoders';
import { ProductLicensesResource } from '../../Types';
import ProductLicensesStore from './Store';
import { ProductLicensesState } from './Types';

interface Props extends EAProps<ProductLicensesStore> {
  learningCollectionId: Maybe<number>;
}

type MissingIdError = { kind: 'missing-id-error' };
type ProductLicensesError = AppyError | MissingLinkError | InvalidUrlError | MissingIdError;

const productLicensesError = (store: ProductLicensesStore) => (error: ProductLicensesError) => {
  switch (error.kind) {
    case 'missing-link-error':
      store.error('Product licenses link is unavailable');
      warn('ProductLicensesReactions', `Missing link: ${error.rel}`, error);
      break;
    case 'invalid-url-error':
      store.error('Product licenses link is unavailable');
      warn('ProductLicensesReactions', `Invalid Url`, error);
      break;
    case 'missing-id-error':
      store.error('Product licenses link is unavailable');
      warn('ProductLicensesReactions', `Missing Learning Collection Id`, error);
      break;
    default:
      handleError(store, error);
  }
};

const productLicensesSuccess =
  (store: ProductLicensesStore) => (productLicensesResource: ProductLicensesResource) => {
    store.ready(productLicensesResource);
  };

const updateHrefWithId =
  (link: Link) =>
  (id: number): Task<InvalidUrlError, Link> =>
    toUrlT(link.href)
      .map(putQueryParam('learning_collection_id', String(id)))
      .map((url): Link => ({ ...link, href: url.href }));

const configureUrl =
  (learningCollectionId: Maybe<number>) =>
  (link: Link): Task<ProductLicensesError, Link> =>
    Task.succeed<ProductLicensesError, Maybe<number>>(learningCollectionId)
      .andThen(toTask({ kind: 'missing-id-error' }))
      .andThen(updateHrefWithId(link))
      .orElse(() => Task.succeed(link));

class ProductLicensesReactions extends ErrorActionableReaction<
  ProductLicensesStore,
  ProductLicensesState,
  Props
> {
  tester = () => this.props.store.state;
  effect = (state: ProductLicensesState) => {
    const { store, learningCollectionId } = this.props;

    switch (state.kind) {
      case 'waiting':
        break;
      case 'loading':
        Task.succeed<ProductLicensesError, ReadonlyArray<Link>>(currentUserStore.links)
          .andThen(findLinkT('product-licenses'))
          .andThen(configureUrl(learningCollectionId))
          .andThen(callApi(productLicensesResourceDecoder, {}))
          .fork(productLicensesError(store), productLicensesSuccess(store));
        break;
      case 'ready':
      case 'error':
        break;
      default:
        assertNever(state);
    }
  };
}

export default ProductLicensesReactions;
